import { useEffect, useMemo, useRef } from 'react';
import { useRouteMatch } from 'react-router-dom';
import _ from 'lodash';

import { useHealthPlanNameStore } from '../BasicProperties/HealthPlanName/stores';
import useGetFormattedCoverageLevels from '../CoverageLevels/hooks/useGetFormattedCoverageLevels';
import { useHealthPlanByIdQuery } from '../queries';
import { usePlanLoadingStore } from '../stores';
import { getFormatedHealthPlan } from '../utils';

import useHealthPlanSetupSetupFieldsSetter from './useHealthPlanSetupFieldsSetter';
import usePlanSetupPermissions, { getHealthPlanFieldState } from './useHealthPlanSetupPermissions';
import usePlanSetupSetter from './useHealthPlanSetupSetter';

export const useCurrentHealthPlan = (isTemplateMode?: boolean) => {
  const {
    params: { id },
  } = useRouteMatch<{ id: string }>();
  const setLoading = usePlanLoadingStore((state) => state.setLoading);
  const { handleSetState: handleSetFieldsState, handleSetSourceState: handleSetSourceFieldsState } =
    useHealthPlanSetupSetupFieldsSetter();

  const { data, isLoading, isSuccess } = useHealthPlanByIdQuery(id, (healthPlanResponse) => {
    const persmissions = getHealthPlanFieldState(healthPlanResponse, isTemplateMode);
    handleSetSourceFieldsState(persmissions);
    handleSetFieldsState(persmissions);
  });

  const healthPlan = useMemo(() => data, [data]);
  const { handleSetState, handleSetSourceState } = usePlanSetupSetter();
  useEffect(() => {
    setLoading(true);
  }, [isSuccess, setLoading]);
  const healthPlanStatus = useHealthPlanNameStore((state) => state.sourceState?.healthPlanStatus);

  const fieldsState = usePlanSetupPermissions(healthPlan);
  const coverageLevels = useGetFormattedCoverageLevels(
    healthPlan ? healthPlan.health_plan_deductible_configs : [],
    healthPlan?.category_spend_limits
  );

  // Track whether the health plan data from API has actually changed.
  // When only coverageLevels reference changes (e.g. due to COVERAGES_LIST query
  // invalidation after creating a custom coverage level), we should only update
  // coverage levels in the store — NOT overwrite basic properties like
  // perPersonSpendLimit / perCategorySpendLimit with stale API values.
  const prevHealthPlanRef = useRef(healthPlan);

  useEffect(() => {
    if (healthPlan) {
      const isHealthPlanChanged = prevHealthPlanRef.current !== healthPlan;
      prevHealthPlanRef.current = healthPlan;

      if (isHealthPlanChanged) {
        const formatedPlan = getFormatedHealthPlan(healthPlan);
        handleSetState({
          ...formatedPlan,
          coverageLevels,
        });
        handleSetSourceState({
          ...formatedPlan,
          coverageLevels,
        });
      }
      setLoading(false);
    }
  }, [healthPlan, handleSetState, setLoading, coverageLevels, handleSetSourceState]);

  return {
    fieldsState,
    isLoading: id ? isLoading : false,
    data: healthPlan,
    planName: healthPlan?.name?.name,
    planYearId: _.toString(healthPlan?.plan_year?.id),
    arePlansEditable: healthPlan?.are_plans_editable,
    planType: healthPlan?.health_plan_types?.health_plan_types,
    deductibleServiceType: healthPlan?.deductibles_by_service_type?.deductibles_by_service_type,
    healthPlanStatus,
    enrollmentMatrixId: _.toString(healthPlan?.enrollment_matrix?.id),
  };
};
